import { ref, watch, onMounted, onUnmounted } from 'vue'
import apiClient from '@/api/client'

const DEBOUNCE_MS = 350
const MIN_QUERY_LENGTH = 3
const MIN_QUERY_LENGTH_NUMERIC = 4
const STORAGE_KEY = 'global_search_scope'
const RECENT_SEARCHES_KEY = 'global_search_recent'
const MAX_RECENT_SEARCHES = 8

const SCOPES = [
  { id: 'all', label: 'in: All', icon: 'mdi-magnify' },
  { id: 'customers', label: 'in: Customers', icon: 'mdi-account-group' },
  { id: 'jobs', label: 'in: Jobs', icon: 'mdi-wrench' },
  { id: 'parts', label: 'in: Parts', icon: 'mdi-package-variant-closed' },
  { id: 'invoices', label: 'in: Invoices', icon: 'mdi-receipt-text' },
  { id: 'locations', label: 'in: Locations', icon: 'mdi-map-marker' },
  { id: 'employees', label: 'in: Employees', icon: 'mdi-badge-account' },
  { id: 'technicians', label: 'in: Technicians', icon: 'mdi-account-hard-hat' },
  { id: 'leads', label: 'in: Leads', icon: 'mdi-account-plus' },
  { id: 'bookings', label: 'in: Bookings', icon: 'mdi-calendar-check' },
  { id: 'work_orders', label: 'in: Work Orders', icon: 'mdi-clipboard-text' },
  { id: 'estimates', label: 'in: Estimates', icon: 'mdi-file-document-edit' },
  { id: 'documents', label: 'in: Documents', icon: 'mdi-file-document-outline' },
  { id: 'tickets', label: 'in: Tickets', icon: 'mdi-ticket-outline' },
  { id: 'tasks', label: 'in: Tasks', icon: 'mdi-checkbox-marked-circle-outline' },
  { id: 'call_logs', label: 'in: Call Logs', icon: 'mdi-phone-log' },
  { id: 'payments', label: 'in: Payments', icon: 'mdi-cash-multiple' },
  { id: 'users', label: 'in: Users', icon: 'mdi-account-circle' },
  { id: 'settings', label: 'in: Settings', icon: 'mdi-cog-outline' },
]

const CATEGORY_CONFIG = {
  customers: { label: 'Customers', icon: 'mdi-account', viewAllRoute: '/crm/customers' },
  locations: { label: 'Locations', icon: 'mdi-map-marker', viewAllRoute: '/crm/customers' },
  jobs: { label: 'Jobs', icon: 'mdi-wrench', viewAllRoute: '/jobs' },
  invoices: { label: 'Invoices', icon: 'mdi-receipt-text', viewAllRoute: '/accounting/invoices' },
  employees: { label: 'Employees', icon: 'mdi-badge-account', viewAllRoute: '/hr/employees' },
  technicians: { label: 'Technicians', icon: 'mdi-account-hard-hat', viewAllRoute: '/production/technicians' },
  bookings: { label: 'Bookings', icon: 'mdi-calendar-check', viewAllRoute: '/jobs' },
  leads: { label: 'Leads', icon: 'mdi-account-plus', viewAllRoute: '/crm/leads' },
  estimates: { label: 'Estimates', icon: 'mdi-file-document-edit', viewAllRoute: '/operations/quotes' },
  work_orders: { label: 'Work Orders', icon: 'mdi-clipboard-text', viewAllRoute: '/jobs/board' },
  documents: { label: 'Documents', icon: 'mdi-file-document-outline', viewAllRoute: '/docs' },
  tickets: { label: 'Support Tickets', icon: 'mdi-ticket-outline', viewAllRoute: '/support/tickets' },
  tasks: { label: 'Tasks', icon: 'mdi-checkbox-marked-circle-outline', viewAllRoute: '/planning/tasks' },
  call_logs: { label: 'Call Logs', icon: 'mdi-phone-log', viewAllRoute: '/dialer/logs' },
  payments: { label: 'Payments', icon: 'mdi-cash-multiple', viewAllRoute: '/accounting/payments' },
  users: { label: 'Users', icon: 'mdi-account-circle', viewAllRoute: '/admin/users' },
  settings: { label: 'Settings', icon: 'mdi-cog-outline', viewAllRoute: '/jobs/settings/job-types' },
  // v3.5 - parts in header search
  parts: { label: 'Parts', icon: 'mdi-package-variant-closed', viewAllRoute: '/operations/inventory' },
}

const CATEGORY_ORDER_DEFAULT = [
  'customers', 'locations', 'jobs', 'parts', 'invoices', 'employees',
  'technicians', 'bookings', 'leads', 'estimates', 'work_orders',
  'documents', 'tickets', 'tasks', 'call_logs', 'payments', 'users', 'settings',
]

const CATEGORY_ORDER_PHONE = [
  'jobs', 'customers', 'locations', 'call_logs', 'invoices', 'work_orders',
  'bookings', 'leads', 'employees', 'technicians', 'payments',
  'estimates', 'documents', 'tickets', 'tasks', 'users', 'settings', 'parts',
]

function _looksLikePhone(q) {
  return /^[\d\-\+\(\)\s]+$/.test(q.trim()) && q.replace(/\D/g, '').length >= 3
}

function _loadRecentSearches() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]')
  } catch { return [] }
}

function _saveRecentSearch(q) {
  if (!q || q.length < MIN_QUERY_LENGTH) return
  try {
    let recent = _loadRecentSearches()
    recent = recent.filter(r => r !== q)
    recent.unshift(q)
    if (recent.length > MAX_RECENT_SEARCHES) recent = recent.slice(0, MAX_RECENT_SEARCHES)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent))
  } catch { /* ignore */ }
}

export function useGlobalSearch() {
  const query = ref('')
  const scope = ref(localStorage.getItem(STORAGE_KEY) || 'all')
  const results = ref({})
  const loading = ref(false)
  const isOpen = ref(false)
  const error = ref(null)
  const activeIndex = ref(-1)
  const recentSearches = ref(_loadRecentSearches())
  const showRecent = ref(false)

  let debounceTimer = null
  let abortController = null
  let lastQueryKey = null
  const clientCache = new Map() // simple per-tab LRU-ish cache, TTL handled inline

  function _isNumericQuery(q) {
    return /^[\d\-\+\(\)\s]+$/.test(q.trim())
  }

  function _minLengthFor(q) {
    return _isNumericQuery(q) ? MIN_QUERY_LENGTH_NUMERIC : MIN_QUERY_LENGTH
  }

  function _queryKey(q, sc) {
    return `${sc}::${q.trim().toLowerCase()}`
  }

  function setScope(newScope) {
    scope.value = newScope
    localStorage.setItem(STORAGE_KEY, newScope)
    const q = query.value.trim()
    if (q.length >= _minLengthFor(q)) {
      performSearch()
    }
  }

  async function performSearch() {
    const q = query.value.trim()
    if (q.length < _minLengthFor(q)) {
      results.value = {}
      isOpen.value = false
      loading.value = false
      return
    }

    const key = _queryKey(q, scope.value)

    // Client-side cache: avoid re-hitting API for identical queries within 30s.
    const cached = clientCache.get(key)
    if (cached && (Date.now() - cached.ts < 30_000)) {
      results.value = cached.data || {}
      isOpen.value = true
      showRecent.value = false
      loading.value = false
      lastQueryKey = key
      return
    }

    // Dedupe: if the same query is already being fetched, skip.
    if (lastQueryKey === key && loading.value) {
      return
    }

    if (abortController) {
      abortController.abort()
    }
    abortController = new AbortController()
    lastQueryKey = key

    loading.value = true
    error.value = null
    activeIndex.value = -1

    try {
      // v3.5 - fan out to TWO endpoints in parallel: the legacy global
      // search and the new parts lookup. The parts lookup is shown
      // under the 'parts' category in the same dropdown.
      const wantParts = scope.value === 'all' || scope.value === 'parts'
      const reqs = [
        apiClient.get('/search/', {
          params: { q, scope: scope.value, limit: 5 },
          signal: abortController.signal,
        }),
      ]
      if (wantParts) {
        reqs.push(apiClient.get('/parts/lookup/search/', {
          params: { q, limit: 5 },
          signal: abortController.signal,
        }).catch(e => {
          console.warn('[GlobalSearch] parts lookup failed:', e?.message || e)
          return { data: { results: [] } }
        }))
      }
      const [globalRes, partsRes] = await Promise.all(reqs)
      const resData = globalRes.data.results || {}
      // Merge in parts results in the dropdown's category shape.
      if (partsRes && partsRes.data.results?.length) {
        resData.parts = {
          items: partsRes.data.results.map(p => ({
            id: p.id,
            title: `${p.part_number} - ${p.name}`,
            subtitle: [
              p.manufacturer || p.brand || '',
              p.qty_total > 0
                ? `${p.qty_total} on hand at ${p.wh_count} wh`
                : 'out of stock',
            ].filter(Boolean).join(' - '),
            icon: 'mdi-package-variant-closed',
            // Hint to the dropdown: open the Part Card dialog instead
            // of navigating to a route.
            _dialog: 'part_card',
            _part_id: p.id,
          })),
          total: partsRes.data.count || partsRes.data.results.length,
        }
      }
      results.value = resData
      isOpen.value = true
      showRecent.value = false
      if (Object.keys(resData).length > 0) {
        _saveRecentSearch(q)
        recentSearches.value = _loadRecentSearches()
      }
      clientCache.set(key, { ts: Date.now(), data: resData })
      if (clientCache.size > 50) {
        const firstKey = clientCache.keys().next().value
        clientCache.delete(firstKey)
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return
      console.error('[GlobalSearch] Error:', err)
      error.value = 'Search failed'
      results.value = {}
    } finally {
      loading.value = false
    }
  }

  function debouncedSearch() {
    clearTimeout(debounceTimer)
    const q = query.value.trim()
    if (q.length < _minLengthFor(q)) {
      results.value = {}
      isOpen.value = false
      loading.value = false
      return
    }
    // Instant cache hit path — no debounce needed.
    const key = _queryKey(q, scope.value)
    const cached = clientCache.get(key)
    if (cached && (Date.now() - cached.ts < 30_000)) {
      results.value = cached.data || {}
      isOpen.value = true
      showRecent.value = false
      loading.value = false
      return
    }
    // Open the dropdown as soon as a valid query starts loading so the
    // "Searching..." spinner is visible immediately. Previously isOpen was
    // only set to true once results returned, so on the first search the
    // desktop dropdown (and its spinner) never rendered while the request
    // was in flight, making the UI look frozen.
    showRecent.value = false
    loading.value = true
    isOpen.value = true
    debounceTimer = setTimeout(performSearch, DEBOUNCE_MS)
  }

  watch(query, debouncedSearch)

  function close() {
    isOpen.value = false
    activeIndex.value = -1
  }

  function clear() {
    query.value = ''
    results.value = {}
    isOpen.value = false
    activeIndex.value = -1
  }

  function currentCategoryOrder() {
    return _looksLikePhone(query.value) ? CATEGORY_ORDER_PHONE : CATEGORY_ORDER_DEFAULT
  }

  function getAllItems() {
    const items = []
    for (const cat of currentCategoryOrder()) {
      const catData = results.value[cat]
      if (catData?.items?.length) {
        for (const item of catData.items) {
          items.push({ ...item, _category: cat })
        }
      }
    }
    return items
  }

  function moveActive(direction) {
    const items = getAllItems()
    if (!items.length) return
    activeIndex.value = Math.max(-1, Math.min(items.length - 1, activeIndex.value + direction))
  }

  function getActiveItem() {
    const items = getAllItems()
    if (activeIndex.value >= 0 && activeIndex.value < items.length) {
      return items[activeIndex.value]
    }
    return null
  }

  function getOrderedCategories() {
    return currentCategoryOrder().filter(cat => results.value[cat]?.items?.length > 0)
  }

  function handleKeyboardShortcut(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault()
      return 'focus'
    }
    if (event.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
      event.preventDefault()
      return 'focus'
    }
    return null
  }

  function cleanup() {
    clearTimeout(debounceTimer)
    if (abortController) abortController.abort()
  }

  function openRecent() {
    if (query.value.length === 0 && recentSearches.value.length > 0) {
      showRecent.value = true
      isOpen.value = true
    }
  }

  function selectRecentSearch(q) {
    query.value = q
    showRecent.value = false
  }

  function clearRecentSearches() {
    recentSearches.value = []
    localStorage.removeItem(RECENT_SEARCHES_KEY)
    showRecent.value = false
  }

  return {
    query,
    scope,
    results,
    loading,
    isOpen,
    error,
    activeIndex,
    recentSearches,
    showRecent,
    setScope,
    close,
    clear,
    moveActive,
    getActiveItem,
    getAllItems,
    getOrderedCategories,
    handleKeyboardShortcut,
    cleanup,
    openRecent,
    selectRecentSearch,
    clearRecentSearches,
    SCOPES,
    CATEGORY_CONFIG,
    currentCategoryOrder,
    MIN_QUERY_LENGTH,
  }
}
